import React, { useEffect, useRef, useState } from 'react';
import { makeMeteor, cloudCovers, starWishOpen, STAR_PERIOD_S, WISH_MAX } from '../../utils/roomPeek';
import { emitGameEvent, GAME_EVENTS } from '../../utils/gameEventBus';

/**
 * 🌕☁️🌠🏮 ROOF NIGHT (09-26, the Harvest Moon): the drawn sky over the room's
 * picture. Timing + selection are pure (roomPeek.nightSkyFor and friends); this
 * module only draws. Everything is placed and sized in % of the painting, so the
 * SAME element reads right on the picture and 2x inside the peek glass (the
 * MoonDisc rule). Everything decorative is aria-hidden + pointer-events none.
 * Sync: an animation's negative delay is read from the real clock ONCE, at
 * mount (a delay that changed on every re-render would jump the animation).
 * Reduced motion: nothing moves (static or nothing).
 * ⭐ No global border-box here: every padded box that takes a width sets it.
 */
const PARCH = '#e6dac6';
const GOLD = '#e9b565';
const INK = 'rgba(38,24,18,0.6)';

export const ROOF_NIGHT_CSS = `
@keyframes rnCloud { 0% { transform: translateX(0); opacity: 0; } 6% { opacity: 0.97; } 94% { opacity: 0.97; } 100% { transform: translateX(100%); opacity: 0; } }
@keyframes rnMeteor { 0% { opacity: 0; transform: rotate(var(--rn-a)) translateX(0) scaleX(0.35); } 18% { opacity: 1; } 100% { opacity: 0; transform: rotate(var(--rn-a)) translateX(-140%) scaleX(1); } }
@keyframes rnStar { 0% { transform: translate(0, 0); opacity: 0; } 3% { opacity: 1; } 24% { opacity: 1; } 30% { transform: translate(-85%, 70%); opacity: 0; } 100% { transform: translate(-85%, 70%); opacity: 0; } }
@keyframes rnRise { 0% { transform: translateY(100%); opacity: 0; } 5% { opacity: 1; } 74% { opacity: 1; } 100% { transform: translateY(0); opacity: 0; } }
@keyframes rnSway { from { transform: translateX(-35%) rotate(-4deg); } to { transform: translateX(35%) rotate(4deg); } }
@keyframes rnShrink { from { transform: translate(-50%, -50%) scale(1); } to { transform: translate(-50%, -50%) scale(0.5); } }
@keyframes rnPound { 0%, 40% { transform: rotate(35deg); } 55%, 62% { transform: rotate(0deg); } 100% { transform: rotate(35deg); } }
@media (prefers-reduced-motion: reduce) { [data-rn-anim] { animation: none !important; } }
`;

/** Live prefers-reduced-motion (jsdom has no matchMedia: motion). */
export function useCalm() {
  const q = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null);
  const [calm, setCalm] = useState(() => { const m = q(); return !!(m && m.matches); });
  useEffect(() => {
    const m = q();
    if (!m) return undefined;
    const on = () => setCalm(!!m.matches);
    if (m.addEventListener) m.addEventListener('change', on); else if (m.addListener) m.addListener(on);
    return () => { if (m.removeEventListener) m.removeEventListener('change', on); else if (m.removeListener) m.removeListener(on); };
  }, []);
  return calm;
}

/** Seconds since `startMs`, read once at mount (the negative animation-delay). */
function useMountElapsed(startMs) {
  const [s] = useState(() => Math.max(0, (Date.now() - Number(startMs)) / 1000) || 0);
  return s;
}

/** The moon's colors (MoonDisc + the eyepiece share them). Harvest: deep
 *  orange low on the horizon, paling to gold as it climbs (p 0 → 1). Blood: embers. */
const lerpHex = (a, b, t) => {
  const x = parseInt(a.slice(1), 16); const y = parseInt(b.slice(1), 16);
  const ch = (s) => Math.round(((x >> s) & 255) + ((((y >> s) & 255) - ((x >> s) & 255)) * t));
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0')}`;
};
export function moonPalette(moon) {
  const p = Math.max(0, Math.min(1, Number(moon && moon.p) || 0));
  const blood = !!moon && moon.tone === 'blood';
  const c = blood
    ? ['#f2a38c', '#c4472d', '#8c2616', '#5c160c']
    : [lerpHex('#ffe6ad', '#fff7df', p), lerpHex('#ffbd66', '#ffe6a6', p), lerpHex('#f08c3c', '#f2c679', p), lerpHex('#c9652a', '#cfa061', p)];
  const glow = blood ? '205,72,48' : (p < 0.5 ? '255,168,88' : '255,214,150');
  return { c, glow, blood, p };
}

// ── ☁️ the twist cloud ──────────────────────────────────────────────────────
// A track 27% of the painting wide, centered on the moon; the cloud (18% wide)
// crosses it over 6 minutes, so it sits ON the moon for the middle third.
const CLOUD_TRACK = 27;
const CLOUD_W = 18;
let cloudSeq = 0;
function CloudShape({ blood }) {
  const [gid] = useState(() => { cloudSeq += 1; return `rnCloud${cloudSeq}`; });
  const rim = blood ? '#b98578' : '#cdb592';
  return (
    <svg viewBox="0 0 200 100" width="100%" height="100%" aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={rim} />
          <stop offset="0.32" stopColor="#6c6581" />
          <stop offset="1" stopColor="#3a3549" />
        </linearGradient>
      </defs>
      <path d="M24 80 C6 80 4 58 21 54 C16 36 38 27 53 38 C58 19 87 13 99 30 C111 11 143 15 147 38 C163 29 188 40 181 58 C197 62 195 81 176 81 Z"
        fill={`url(#${gid})`} stroke={INK} strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M44 62 C60 58 76 64 92 60 M112 66 C128 61 146 66 160 62" fill="none" stroke="rgba(255,236,205,0.18)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function MoonCloud({ moon, cloud, calm }) {
  const elapsed = useMountElapsed(cloud.start);
  if (calm && !cloudCovers(cloud)) return null;
  const still = calm;
  return (
    <span data-peek-cloud={moon.tone} aria-hidden="true" style={{
      position: 'absolute', left: `${moon.x - CLOUD_TRACK / 2}%`, top: `${moon.y}%`, width: `${CLOUD_TRACK}%`, height: 0,
      pointerEvents: 'none', transition: 'top 14s linear',
    }}>
      <span data-rn-anim="" style={{
        position: 'absolute', left: 0, top: 0, width: '100%', height: 0,
        ...(still ? { transform: 'translateX(50%)' } : { animation: `rnCloud ${cloud.durSec}s linear -${elapsed.toFixed(2)}s both` }),
      }}>
        <span style={{ position: 'absolute', left: 0, top: 0, width: `${(CLOUD_W / CLOUD_TRACK) * 100}%`, aspectRatio: '2 / 1', transform: 'translate(-50%, -50%)' }}>
          <CloudShape blood={moon.tone === 'blood'} />
        </span>
      </span>
    </span>
  );
}

// ── 🌠 meteors (a meteor-shower night): one every 2-6s, ~0.8s each ─────────
/** The lightbox owns the list so the picture and the glass draw the SAME ones. */
export function useMeteors(box, calm) {
  const [list, setList] = useState([]);
  const key = Array.isArray(box) ? box.join(',') : '';
  useEffect(() => {
    if (!key || calm) { setList([]); return undefined; }
    const b = key.split(',').map(Number);
    let alive = true; let next = 0; let seq = 0;
    const gone = new Set();
    const spawn = () => {
      if (!alive) return;
      seq += 1;
      const m = makeMeteor(b, Math.random, seq);
      if (m) {
        setList((l) => [...l.slice(-3), m]);
        const k = setTimeout(() => { gone.delete(k); if (alive) setList((l) => l.filter((x) => x.id !== m.id)); }, 1000);
        gone.add(k);
      }
      next = setTimeout(spawn, 2000 + Math.random() * 4000);
    };
    next = setTimeout(spawn, 700 + Math.random() * 1500);
    return () => { alive = false; clearTimeout(next); gone.forEach((k) => clearTimeout(k)); };
  }, [key, calm]);
  return list;
}
function Meteor({ m }) {
  return (
    <span data-peek-meteor="" data-rn-anim="" aria-hidden="true" style={{
      position: 'absolute', left: `${m.x}%`, top: `${m.y}%`, width: `${m.len}%`, height: 'max(1.5px, 0.4%)',
      borderRadius: 999, pointerEvents: 'none', transformOrigin: '0 50%', '--rn-a': `${m.angle}deg`,
      background: 'linear-gradient(90deg, #fffdf2 0%, rgba(255,246,220,0.85) 10%, rgba(255,236,200,0) 100%)',
      animation: 'rnMeteor 0.8s ease-out both',
    }} />
  );
}

// ── 🌠 the falling star (a shooting-star card under 60s old, not caught) ─────
function FallingStar({ star }) {
  const elapsed = useMountElapsed(star.start);
  const [over, setOver] = useState(false);
  useEffect(() => {
    const ms = Number(star.endsAt) - Date.now();
    if (!(ms > 0)) { setOver(true); return undefined; }
    const id = setTimeout(() => setOver(true), ms);
    return () => clearTimeout(id);
  }, [star.endsAt]);
  if (over) return null;
  const [x0, y0, x1, y1] = star.box;
  return (
    <span data-peek-star="" aria-hidden="true" style={{
      position: 'absolute', left: `${x0}%`, top: `${y0}%`, width: `${x1 - x0}%`, height: `${y1 - y0}%`, pointerEvents: 'none',
    }}>
      <span data-rn-anim="" style={{
        position: 'absolute', left: 0, top: 0, width: '100%', height: '100%',
        animation: `rnStar ${STAR_PERIOD_S}s linear -${(elapsed % STAR_PERIOD_S).toFixed(2)}s infinite`,
      }}>
        <span style={{ position: 'absolute', left: '95%', top: '8%', width: '30%', height: 'max(2.5px, 3%)', transformOrigin: '0 50%', transform: 'rotate(-8deg)' }}>
          <span style={{ position: 'absolute', left: 0, top: '50%', width: '45%', height: '600%', transform: 'translate(-12%, -50%)', borderRadius: '50%', background: 'radial-gradient(ellipse at 12% 50%, rgba(255,240,200,0.6), rgba(255,240,200,0) 70%)' }} />
          <span style={{ position: 'absolute', inset: 0, borderRadius: 999, background: 'linear-gradient(90deg, #ffffff 0%, #fff6d6 5%, rgba(255,232,170,0.75) 20%, rgba(255,220,150,0) 100%)' }} />
        </span>
      </span>
    </span>
  );
}

// ── 🏮 wish lanterns: up from the quilts to the top of the sky ────────────────
function LanternDrawing({ gold }) {
  const [gid] = useState(() => { cloudSeq += 1; return `rnLantern${cloudSeq}`; });
  const stops = gold ? ['#fffbe2', '#ffd966', '#e39a2f'] : ['#fff0bf', '#ffac52', '#d9642a'];
  return (
    <svg viewBox="0 0 30 40" width="100%" height="100%" aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <radialGradient id={gid} cx="0.5" cy="0.78" r="0.8">
          <stop offset="0" stopColor={stops[0]} />
          <stop offset="0.45" stopColor={stops[1]} />
          <stop offset="1" stopColor={stops[2]} />
        </radialGradient>
      </defs>
      <path d="M4 4 H26 Q28.2 4 27.7 8 L24 33 Q23.6 35.2 21 35.2 H9 Q6.4 35.2 6 33 L2.3 8 Q1.8 4 4 4 Z" fill={`url(#${gid})`} stroke="#3a2012" strokeOpacity="0.8" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M11 5 L12 34 M19 5 L18 34" stroke="rgba(120,50,20,0.32)" strokeWidth="0.8" fill="none" />
      <rect x="8.5" y="34.4" width="13" height="2.4" rx="1" fill="#5a2e16" />
      <ellipse cx="15" cy="32.6" rx="2" ry="1.4" fill="#fff8d6" />
    </svg>
  );
}
function Lantern({ l, calm }) {
  const elapsed = useMountElapsed(l.start);
  const w = l.gold ? 3 : 2.4;
  const halo = l.gold ? 'rgba(255,226,120,0.72), rgba(255,210,90,0.22) 38%' : 'rgba(255,190,90,0.5), rgba(255,160,70,0.16) 36%';
  const body = (anim) => (
    <span data-rn-anim={anim ? '' : undefined} style={{
      position: 'absolute', left: '50%', top: 0, width: '100%', aspectRatio: '3 / 4',
      ...(anim ? { animation: `rnShrink 90s linear -${elapsed.toFixed(2)}s both` } : { transform: 'translate(-50%, -50%) scale(0.75)' }),
    }}>
      <span style={{ position: 'absolute', left: '50%', top: '62%', width: '330%', aspectRatio: '1 / 1', transform: 'translate(-50%, -50%)', borderRadius: '50%', background: `radial-gradient(circle, ${halo}, rgba(255,150,60,0) 66%)` }} />
      <LanternDrawing gold={l.gold} />
    </span>
  );
  if (calm) {
    return (
      <span data-peek-lantern={l.gold ? 'gold' : 'paper'} aria-hidden="true" style={{ position: 'absolute', left: `${l.x}%`, top: `${l.rest}%`, width: `${w}%`, height: 0, pointerEvents: 'none' }}>
        {body(false)}
      </span>
    );
  }
  return (
    <span data-peek-lantern={l.gold ? 'gold' : 'paper'} aria-hidden="true" style={{ position: 'absolute', left: `${l.x}%`, top: '4%', width: `${w}%`, height: '66%', pointerEvents: 'none' }}>
      <span data-rn-anim="" style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', animation: `rnRise 90s cubic-bezier(.3,.15,.6,1) -${elapsed.toFixed(2)}s both` }}>
        <span data-rn-anim="" style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: 0, animation: `rnSway ${l.sway}s ease-in-out -${(elapsed % (2 * l.sway)).toFixed(2)}s infinite alternate` }}>
          {body(true)}
        </span>
      </span>
    </span>
  );
}

/** Everything in the sky that is not the moon, for the picture AND the glass.
 *  Order = paint order: lanterns low in the sky, the cloud over the moon,
 *  meteors and the star on top. */
export function NightLayer({ night, moon, meteors = [], calm = false }) {
  const cloud = night && night.cloud && moon ? night.cloud : null;
  const lanterns = (night && night.lanterns) || [];
  const star = night && night.star && !calm ? night.star : null;
  if (!cloud && !lanterns.length && !star && !(meteors && meteors.length)) return null;
  return (
    <>
      {lanterns.map((l) => <Lantern key={l.id} l={l} calm={calm} />)}
      {cloud && <MoonCloud key={cloud.id} moon={moon} cloud={cloud} calm={calm} />}
      {!calm && (meteors || []).map((m) => <Meteor key={m.id} m={m} />)}
      {star && <FallingStar key={star.id} star={star} />}
    </>
  );
}

// ── 🔭 the telescope's eyepiece ─────────────────────────────────────────────
function EyepieceMoon({ moon, calm }) {
  const { c, blood } = moonPalette(moon);
  const [gid] = useState(() => { cloudSeq += 1; return `rnEye${cloudSeq}`; });
  const shade = blood ? '#4a1109' : c[3];
  return (
    <svg data-peek-eyepiece-moon={moon && moon.tone} viewBox="0 0 200 200" width="100%" height="100%" aria-hidden="true" style={{ display: 'block' }}>
      <defs>
        <radialGradient id={gid} cx="0.4" cy="0.36" r="0.72">
          <stop offset="0" stopColor={c[0]} />
          <stop offset="0.42" stopColor={c[1]} />
          <stop offset="0.8" stopColor={c[2]} />
          <stop offset="1" stopColor={c[3]} />
        </radialGradient>
      </defs>
      <circle cx="100" cy="100" r="92" fill={`url(#${gid})`} stroke="rgba(64,30,10,0.6)" strokeWidth="2.4" />
      {[[62, 58, 11], [143, 70, 8], [58, 146, 9], [150, 150, 13], [98, 40, 6], [165, 110, 6]].map(([x, y, r]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill={shade} fillOpacity="0.2" />
      ))}
      {/* the Moon Rabbit pounding mochi, a shade darker than the moon */}
      <g data-peek-rabbit="" fill={shade} fillOpacity="0.5" stroke="none">
        <ellipse cx="124" cy="122" rx="21" ry="28" />
        <circle cx="112" cy="86" r="15" />
        <ellipse cx="121" cy="58" rx="5.5" ry="19" transform="rotate(18 121 58)" />
        <ellipse cx="131" cy="62" rx="5" ry="17" transform="rotate(34 131 62)" />
        <circle cx="145" cy="130" r="7" />
        <ellipse cx="130" cy="149" rx="16" ry="6" />
        <path d="M115 101 L104 105" stroke={shade} strokeOpacity="1" strokeWidth="7" strokeLinecap="round" />
        <path d="M52 128 H98 L92 158 H58 Z" />
        <rect x="54" y="156" width="42" height="6" rx="2" />
        <ellipse cx="75" cy="127" rx="19" ry="7" />
        <g data-rn-anim="" style={{ transformBox: 'view-box', transformOrigin: '104px 105px', ...(calm ? { transform: 'rotate(20deg)' } : { animation: 'rnPound 1.4s ease-in-out infinite' }) }}>
          <line x1="104" y1="105" x2="75" y2="114" stroke={shade} strokeOpacity="1" strokeWidth="4.5" strokeLinecap="round" />
          <rect x="64" y="105" width="13" height="20" rx="3" transform="rotate(-19 70.5 115)" />
        </g>
      </g>
    </svg>
  );
}
/** A big round view through the telescope. Click anywhere (or Esc, the
 *  caller's) closes it; the picture stays open under it. */
export function TelescopeEyepiece({ moon, line, onClose, calm = false }) {
  return (
    <div data-peek-eyepiece="" role="dialog" aria-label="Through the telescope"
      onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); }}
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', inset: 0, zIndex: 3, boxSizing: 'border-box', padding: 16, cursor: 'zoom-out',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
        background: 'radial-gradient(circle at 50% 45%, rgba(4,3,2,0.9) 0%, rgba(2,1,1,0.98) 70%)',
      }}>
      <div aria-hidden="true" style={{
        position: 'relative', width: 'min(70vmin, 520px)', aspectRatio: '1 / 1', borderRadius: '50%', overflow: 'hidden', boxSizing: 'border-box',
        border: '6px solid #c9a15a', boxShadow: '0 0 0 3px #1d150e, 0 0 60px rgba(0,0,0,0.9)',
        background: 'radial-gradient(circle at 50% 50%, #1c2544 0%, #0c1124 68%, #04060d 100%)',
      }}>
        {[[14, 22], [80, 16], [88, 70], [22, 82], [50, 8], [9, 55]].map(([x, y]) => (
          <span key={`${x}-${y}`} style={{ position: 'absolute', left: `${x}%`, top: `${y}%`, width: 3, height: 3, borderRadius: '50%', background: '#f6ecd2', opacity: 0.8 }} />
        ))}
        <div style={{ position: 'absolute', left: '9%', top: '9%', width: '82%', height: '82%' }}>
          <EyepieceMoon moon={moon} calm={calm} />
        </div>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,0,0,0) 58%, rgba(0,0,0,0.78) 100%)' }} />
      </div>
      {line && <div data-testid="eyepiece-line" aria-live="polite" style={{ color: PARCH, fontSize: 15, fontStyle: 'italic', textAlign: 'center', maxWidth: 'min(520px, 100%)', lineHeight: 1.45 }}>{line}</div>}
    </div>
  );
}

// ── 🌠 the wish box (roof only, while the verb is live) ───────────────────────
const stop = (e) => e.stopPropagation();
export function WishButton({ open, onToggle }) {
  return (
    <button type="button" data-peek-wish="button" aria-label="Make a wish" aria-expanded={open ? 'true' : 'false'} title="Make a wish"
      onClick={(e) => { e.stopPropagation(); if (onToggle) onToggle(); }}
      style={{
        position: 'absolute', top: 10, right: 10, height: 34, padding: '0 13px', display: 'inline-flex', alignItems: 'center', gap: 6,
        borderRadius: 999, cursor: 'pointer', font: 'inherit', fontSize: 13.5, fontWeight: 700, lineHeight: 1, textShadow: 'none',
        background: '#2c2117', border: `1px solid ${GOLD}`, color: GOLD, boxSizing: 'border-box', boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
      }}>
      <span aria-hidden="true">🌠</span>Wish
    </button>
  );
}
// ── 🔍📸🔗 09-26 (Nina): the event scene's tools ─────────────────────────────
const roundBtn = (on) => ({
  width: 34, height: 34, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 999, cursor: 'pointer', font: 'inherit', fontSize: 16, lineHeight: 1, textShadow: 'none',
  background: on ? GOLD : '#2c2117', border: `1px solid ${GOLD}`, color: on ? '#2a1c08' : GOLD,
  boxSizing: 'border-box', boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
});
const SCENE_CSS = `
@keyframes rnFlash { from { opacity: 0.85; } to { opacity: 0; } }
.rn-flash { animation: rnFlash 0.45s ease-out forwards; }
@keyframes rnSnapHot { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,214,140,0.8); } 50% { box-shadow: 0 0 0 6px rgba(255,214,140,0); } }
.rn-snap-hot { animation: rnSnapHot 1.1s ease-out infinite; }
@keyframes rnTeleIdle { 0%, 100% { opacity: 0.22; } 50% { opacity: 0.45; } }
.rn-tele-idle { animation: rnTeleIdle 3.2s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .rn-flash { animation: none; opacity: 0; } .rn-snap-hot { animation: none; } .rn-tele-idle { animation: none; opacity: 0.32; } }
`;

/** 🔭 09-26 (Nina): a clickable thing in the scene. `idle` = a very light breathing
 *  glow until it has been used once; `hot` = lit up under the pointer. */
export function TeleGlow({ x, y, hot = false, idle = true }) {
  if (!hot && !idle) return null;
  return (
    <>
      <style>{SCENE_CSS}</style>
      <span data-peek-interactive="telescope" data-hot={hot ? '1' : '0'} aria-hidden="true" className={hot ? undefined : 'rn-tele-idle'} style={{
        position: 'absolute', left: `${x}%`, top: `${y}%`, width: '11%', aspectRatio: '1 / 1', transform: 'translate(-50%, -50%)',
        borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(255,214,140,0.7) 0%, rgba(255,214,140,0.28) 38%, rgba(255,214,140,0) 68%)',
        boxShadow: hot ? '0 0 0 2px rgba(255,214,140,0.7), 0 0 18px 4px rgba(255,214,140,0.4)' : 'none',
        opacity: hot ? 0.95 : undefined, transition: 'opacity 0.25s ease, box-shadow 0.25s ease',
      }} />
    </>
  );
}

/** One row right under Wish (Wish's own spot when there is none), so it fits
 *  the short phone picture: 🔍 the glass (put away until pressed), 📸 a
 *  polaroid of the scene (a camera flash on the picture; the photo lands in the
 *  chat), 🔗 the scene's link. Every piece is optional. */
export function SceneButtons({ belowWish = false, glass = null, onSnap = null, onShare = null, snapHot = false }) {
  const [flashKey, setFlashKey] = useState(0);
  const [shared, setShared] = useState(false);
  useEffect(() => {
    if (!shared) return undefined;
    const t = setTimeout(() => setShared(false), 1600);
    return () => clearTimeout(t);
  }, [shared]);
  const stop = (e) => e.stopPropagation();
  return (
    <>
      <style>{SCENE_CSS}</style>
      {flashKey > 0 && (
        <div key={flashKey} className="rn-flash" aria-hidden="true"
          style={{ position: 'absolute', inset: 0, background: '#fff', opacity: 0, pointerEvents: 'none', borderRadius: 8 }} />
      )}
      <div data-peek-scene-tools="1" onClick={stop} onPointerDown={stop} style={{
        position: 'absolute', top: belowWish ? 52 : 10, right: 10, display: 'flex', flexDirection: 'row-reverse', gap: 8,
      }}>
        {glass && (
          <button type="button" data-peek-glass-toggle={glass.on ? 'on' : 'off'} aria-label="Magnifying glass" aria-pressed={glass.on ? 'true' : 'false'}
            title={glass.on ? 'Put the glass away' : 'Look closer'} onClick={() => { if (glass.onToggle) glass.onToggle(); }} style={roundBtn(glass.on)}>
            <span aria-hidden="true">🔍</span>
          </button>
        )}
        {onSnap && (
          <button type="button" data-peek-snap={snapHot ? 'gerald' : '1'} aria-label={snapHot ? 'Snap Gerald on the moon' : 'Take a picture'}
            title={snapHot ? 'Snap Gerald on the moon!' : 'Take a picture (it goes in the chat)'} className={snapHot ? 'rn-snap-hot' : undefined}
            onClick={() => { setFlashKey((k) => k + 1); onSnap(); }} style={roundBtn(snapHot)}>
            <span aria-hidden="true">📸</span>
          </button>
        )}
        {onShare && (
          <button type="button" data-peek-share="1" aria-label="Share this scene" title={shared ? 'Link ready' : 'Share this scene'}
            onClick={() => { onShare(); setShared(true); }} style={roundBtn(shared)}>
            <span aria-hidden="true">{shared ? '✓' : '🔗'}</span>
          </button>
        )}
      </div>
    </>
  );
}
/** One line, Enter sends through the composer's own /wish path (onWish), the
 *  caller's Esc closes it first. 💬 09-26 option C: `docked` = it sits in the
 *  phone scene's bottom dock (the message box's slot) instead of over the
 *  picture's bottom edge. */
export function WishBar({ onWish, onClose, docked = false }) {
  const [text, setText] = useState('');
  const [state, setState] = useState(null); // null | 'sending' | 'sent' | 'error'
  const inputRef = useRef(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  useEffect(() => { try { if (inputRef.current) inputRef.current.focus(); } catch (e) { /* focus is a nicety */ } }, []);
  useEffect(() => {
    if (state !== 'sent') return undefined;
    const id = setTimeout(() => { if (onClose) onClose(); }, 2400);
    return () => clearTimeout(id);
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
  const clean = text.replace(/\s+/g, ' ').trim();
  const send = async () => {
    if (!clean || state === 'sending' || !onWish) return;
    setState('sending');
    try {
      await onWish(clean.slice(0, WISH_MAX));
      if (alive.current) { setText(''); setState('sent'); }
    } catch (e) {
      if (alive.current) setState('error');
    }
  };
  return (
    <div data-peek-wish="bar" onClick={stop} onPointerDown={stop} onPointerUp={stop} onPointerMove={stop}
      style={{
        // 💬 09-26 option C: docked in the phone scene's bottom dock, else over the picture's bottom edge
        ...(docked ? { position: 'relative', width: '100%' } : { position: 'absolute', left: '50%', bottom: 10, transform: 'translateX(-50%)', width: 'min(440px, calc(100% - 20px))' }),
        boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px 4px 14px', minHeight: 40, borderRadius: 999,
        background: '#2c2117', border: `1px solid ${GOLD}`, boxShadow: '0 4px 18px rgba(0,0,0,0.6)', cursor: 'auto', touchAction: 'auto', userSelect: 'text', WebkitUserSelect: 'text',
      }}>
      {state === 'sent' ? (
        <span data-testid="wish-sent" aria-live="polite" style={{ flex: '1 1 auto', color: GOLD, fontSize: 14, fontWeight: 700, padding: '6px 0' }}>Sent up. Watch the sky.</span>
      ) : (
        <>
          <input ref={inputRef} type="text" aria-label="Your wish" placeholder="Make a wish…" maxLength={WISH_MAX}
            value={text} onChange={(e) => { setText(e.target.value); if (state === 'error') setState(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !(e.nativeEvent && e.nativeEvent.isComposing)) { e.preventDefault(); send(); } }}
            style={{ flex: '1 1 auto', minWidth: 0, width: '100%', boxSizing: 'border-box', height: 32, padding: '0 2px', border: 0, outline: 'none', background: 'transparent', color: PARCH, font: 'inherit', fontSize: 16 }} />
          {state === 'error' && <span aria-live="polite" style={{ color: '#ff9f7a', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>Try again</span>}
          <button type="button" onClick={(e) => { e.stopPropagation(); send(); }} disabled={!clean || state === 'sending'}
            style={{
              flex: '0 0 auto', height: 32, padding: '0 14px', borderRadius: 999, border: 0, cursor: clean ? 'pointer' : 'default', boxSizing: 'border-box',
              background: GOLD, color: '#2a1c08', font: 'inherit', fontSize: 13.5, fontWeight: 800, opacity: (!clean || state === 'sending') ? 0.55 : 1,
            }}>
            {state === 'sending' ? '…' : 'Send'}
          </button>
        </>
      )}
    </div>
  );
}

// ── 🌠 the Go on the shooting-star card ─────────────────────────────────────
/** "🌠 Wish ▶" while the star is catchable and the roof is open. `bus` =
 *  GameStepPage's eventGoBus ({ rooms, wishLive, starCaughtAt }, written each
 *  render); a 1s tick while open retires the button on the catch or the minute. */
export function StarWishGo({ createdAt, bus }) {
  const [now, setNow] = useState(() => Date.now());
  const open = starWishOpen(createdAt, now, bus && bus.starCaughtAt);
  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open]);
  const roofOpen = !!(bus && Array.isArray(bus.rooms) && bus.rooms.some((r) => r && r.id === 'roof'));
  if (!open || !roofOpen || !(bus && bus.wishLive)) return null;
  return (
    <div style={{ padding: '0 12px 10px' }}>
      <button type="button" data-star-wish-go="roof"
        onClick={(e) => { e.stopPropagation(); emitGameEvent(GAME_EVENTS.EVENT_GO, { room: 'roof', wish: true }); }}
        style={{
          padding: '7px 16px', minHeight: 36, borderRadius: 999, border: 'none', cursor: 'pointer',
          background: '#ffc864', color: '#2a1c08', fontFamily: 'inherit', fontWeight: 800, fontSize: '0.85em',
          letterSpacing: '0.02em', boxShadow: '0 2px 10px rgba(255,200,100,0.35)', touchAction: 'manipulation', boxSizing: 'border-box',
          textShadow: 'none', // it sits in the chat column: no halo on gold paper
        }}>
        🌠 Wish ▶
      </button>
    </div>
  );
}

// ── 📰 the picture's news line ───────────────────────────────────────────────
const NEWS_CSS = `
@keyframes rnNewsIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
.rn-news-in { animation: rnNewsIn 0.5s ease-out; }
@media (prefers-reduced-motion: reduce) { .rn-news-in { animation: none; } }
`;

/** Under the picture on an event night: the house's newest card (the full
 *  screen covers the chat) and the open vote as tap buttons. `news` =
 *  { card: { key, text, star } | null, poll | null, onVote(optionId) | null };
 *  `onWish` opens the wish box (the star card's "Wish now"). */
export function PictureNews({ news, onWish = null }) {
  const card = news && news.card;
  const poll = news && news.poll;
  if (!card && !poll) return null;
  const canVote = !!(news && news.onVote);
  return (
    <div data-peek-news="1" onClick={(e) => e.stopPropagation()} style={{
      width: '100%', maxWidth: 'min(720px, 100%)', boxSizing: 'border-box', cursor: 'default',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    }}>
      <style>{NEWS_CSS}</style>
      {card && (
        <div key={card.key} data-peek-news-card="1" className="rn-news-in" aria-live="polite" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 10,
          maxWidth: '100%', boxSizing: 'border-box', padding: '8px 14px', borderRadius: 14,
          background: '#241a10', border: '1px solid rgba(255,200,100,0.55)', color: '#f6e7c8',
          fontSize: 14, lineHeight: 1.35, textAlign: 'center',
        }}>
          <span>{card.text}</span>
          {card.star && onWish && (
            <button type="button" data-peek-news-wish="1" onClick={onWish} style={{
              padding: '5px 12px', minHeight: 32, borderRadius: 999, border: 'none', cursor: 'pointer',
              background: '#ffc864', color: '#2a1c08', fontFamily: 'inherit', fontWeight: 800, fontSize: 13,
              boxSizing: 'border-box', touchAction: 'manipulation',
            }}>🌠 Wish now</button>
          )}
        </div>
      )}
      {poll && (
        <div role="group" aria-label={poll.question} data-peek-news-poll={poll.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
          <div style={{ color: '#ffc864', fontSize: 14, fontWeight: 700, textAlign: 'center' }}>{poll.question}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6 }}>
            {poll.options.map((o) => {
              const mine = poll.my_vote === o.id;
              return (
                <button key={o.id} type="button" data-peek-news-option={o.id} disabled={!canVote} aria-pressed={mine}
                  onClick={() => { if (canVote) news.onVote(o.id); }}
                  style={{
                    padding: '6px 12px', minHeight: 34, borderRadius: 999, boxSizing: 'border-box', touchAction: 'manipulation',
                    border: mine ? '2px solid #ffc864' : '1px solid rgba(255,200,100,0.45)',
                    background: mine ? '#ffc864' : '#2a1f14', color: mine ? '#2a1c08' : '#f6e7c8',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: canVote ? 'pointer' : 'default',
                  }}>
                  {o.emoji ? `${o.emoji} ` : ''}{o.label}{poll.my_vote ? ` · ${Number(o.votes) || 0}` : ''}{mine ? ' ✓' : ''}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
